/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useCallback, useRef } from 'react'
import Link from 'next/link'
import Searchbar from '@components/common/Searchbar'
import UserNav from '@components/common/UserNav/UserNav'
import { PWALogo } from '@components/icons'
import { RichContent } from '@corratech/pylot-rich-content'
import { useTranslation } from 'react-i18next'
import type { CategoryTree, Maybe } from '@pylot-data/pylotschema'
import cn from 'classnames'
import s from '@commonStyles/menus/MobileMegaMenu.module.scss'
import {
    getMenuItemSlug,
    getMenuItemStableClassNames
} from '../../utils/getMenuItemStableClassNames'
import { isEspaciosMegaMenu } from '../../utils/megamenuCategory'
import EspaciosCasaMobilePanel from '../MenuItem/EspaciosCasaMobilePanel'
// TODO: descomentar cuando PRODUCTOS, NUEVOS, NIÑOIDEAS y OFERTAS
// vuelvan a abrir el drill nuevo en vez de la solapa/link de master.
// import CategoryMobileDrillPanel, {
//     type DrillLevel
// } from '../MenuItem/CategoryMobileDrillPanel'
import { elementIsOnScreen, subMenuAction } from '@lib/utils/DOM'
import MenuTrackOrder from '@components/icons/MenuTrackOrder'
import MenuFindStore from '@components/icons/MenuFindStore'
import MenuFaq from '@components/icons/MenuFaq'
import MenuChat from '@components/icons/MenuChat'
import MenuHelpCenter from '@components/icons/MenuHelpCenter'
import { FOOTER_URLS } from '@components/common/Footer/footer-config'
import FlagChile from '@components/icons/FlagChile'
import FlagBolivia from '@components/icons/FlagBolivia'
import FlagColombia from '@components/icons/FlagColombia'
import FlagPeru from '@components/icons/FlagPeru'
import FlagMexico from '@components/icons/FlagMexico'
import router from 'next/router'
import { isMobileScreen } from '@lib/utils/mediaQuery'
import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks
} from 'body-scroll-lock'

type MobileMegaMenuProps = {
    isOpen: boolean
    setIsOpen: (newIsOpen: boolean) => void
    navData?: CategoryTree
}

type ZendeskCommand = (...args: unknown[]) => void

const openEvaChat = (): void => {
    const zE = (window as Window & { zE?: ZendeskCommand }).zE
    if (typeof zE !== 'function') return
    try {
        zE('messenger', 'open')
    } catch {
        zE('webWidget', 'open')
    }
}

const FLAGS = [
    { name: 'Chile', flagComponent: <FlagChile />, url: '' },
    {
        name: 'Bolivia',
        flagComponent: <FlagBolivia />,
        url: 'https://www.casaideas.com.bo/'
    },
    {
        name: 'Colombia',
        flagComponent: <FlagColombia />,
        url: 'https://www.casaideas.com.co/'
    },
    {
        name: 'Perú',
        flagComponent: <FlagPeru />,
        url: 'https://www.casaideas.com.pe/'
    },
    {
        name: 'México',
        flagComponent: <FlagMexico />,
        url: 'https://www.casaideas-mexico.mx/'
    }
]

const renderCategories = (
    items: Maybe<Array<Maybe<CategoryTree>>>,
    handleCategoryItemClick: (
        item: Maybe<CategoryTree>,
        isOpen?: boolean
    ) => void
) => {
    return items?.map((item) => {
        const url = item?.external_url || `/categoria/${item?.url_key}`
        if (!item?.include_in_menu) return null

        const slug = item ? getMenuItemSlug(item) : undefined
        // TODO: descomentar para que todos los ítems abran panel.
        // const opensPanel = true
        const opensPanel =
            isEspaciosMegaMenu(item) || Boolean(item?.cms_block?.content)

        return (
            item && (
                <div
                    key={url}
                    className={cn(
                        s['menu-item'],
                        getMenuItemStableClassNames(item)
                    )}
                    data-menu-url-key={slug}
                >
                    {opensPanel ? (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={() => handleCategoryItemClick(item, true)}
                            onKeyDown={() =>
                                handleCategoryItemClick(item, true)
                            }
                        >
                            <p>{item.name}</p>
                        </span>
                    ) : (
                        <Link href={url}>
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={() => handleCategoryItemClick(item)}
                                onKeyDown={() => handleCategoryItemClick(item)}
                            >
                                <span>{item.name}</span>
                            </span>
                        </Link>
                    )}
                </div>
            )
        )
    })
}

export const MobileMegaMenu = ({
    isOpen,
    setIsOpen,
    navData
}: MobileMegaMenuProps): JSX.Element | null => {
    const { t } = useTranslation('common')
    const [subMenu, setSubMenu] = useState<{
        back: Maybe<string> | undefined
        seeAllURL: Maybe<string>
        content: Maybe<string> | undefined
    }>({
        back: 'Back button',
        seeAllURL: '/',
        content: null
    })

    const categoriesContainer =
        useRef() as React.MutableRefObject<HTMLDivElement>

    const [showEspacios, setShowEspacios] = useState(false)
    const [espaciosItem, setEspaciosItem] =
        useState<Maybe<CategoryTree>>(null)
    // TODO: descomentar junto con CategoryMobileDrillPanel.
    // const [drillStack, setDrillStack] = useState<DrillLevel[]>([])

    useEffect(() => {
        if (!isOpen) {
            setSubMenu({ back: '', content: null, seeAllURL: '' })
            setShowEspacios(false)
            setEspaciosItem(null)
            // setDrillStack([])
        }
    }, [isOpen])

    useEffect(() => {
        const el = categoriesContainer.current
        if (!el) return

        if (isOpen) {
            disableBodyScroll(el, {
                allowTouchMove: (target) =>
                    Boolean(target && (target === el || el.contains(target)))
            })
        } else {
            enableBodyScroll(el)
        }

        return () => {
            enableBodyScroll(el)
            clearAllBodyScrollLocks()
        }
    }, [isOpen])

    useEffect(() => {
        const handleCloseMobileMenu = () => setIsOpen(false)
        router.events.on('routeChangeComplete', handleCloseMobileMenu)
        return () =>
            router.events.off('routeChangeComplete', handleCloseMobileMenu)
    }, [setIsOpen, router.events])

    useEffect(() => {
        function handleMobileMenu() {
            elementIsOnScreen(
                '.menuCmsContentMobile .submenu-option',
                (elements) => {
                    elements.forEach((el) => {
                        el.onclick = ({ target }) => {
                            categoriesContainer?.current?.scrollTo(0, 0)
                            try {
                                const { classList, parentElement } =
                                    target as HTMLElement
                                const subMenuToShow = subMenuAction(classList)
                                const backButton =
                                    document.querySelector('div[role="button"]')
                                const subSubMenuBackButton =
                                    backButton?.cloneNode(true) as HTMLElement
                                const [subMenuClass] = [...classList].filter(
                                    (el) => el.includes('js')
                                )
                                const subSubMenuName = subMenuClass
                                    .split('-')
                                    .slice(1, -1)
                                    .join(' ')
                                subSubMenuBackButton.innerHTML = `<p>${subSubMenuName}</p>`
                                backButton?.insertAdjacentElement(
                                    'afterend',
                                    subSubMenuBackButton
                                )
                                backButton?.classList.add('hidden')
                                parentElement?.classList.add('hidden')

                                subSubMenuBackButton.onclick = ({ target }) => {
                                    const { classList } = target as HTMLElement
                                    classList.add('hidden')
                                    backButton?.classList.replace(
                                        'hidden',
                                        'block'
                                    )
                                    parentElement?.classList.replace(
                                        'hidden',
                                        'block'
                                    )
                                    subMenuToShow?.classList.replace(
                                        'block',
                                        'hidden'
                                    )
                                }

                                if (subMenuToShow) {
                                    subMenuToShow.classList.replace(
                                        'hidden',
                                        'block'
                                    )
                                }
                            } catch (error) {
                                console.error(error)
                            }
                        }
                    })
                }
            )
        }

        if (isMobileScreen()) handleMobileMenu()
    }, [subMenu])

    const handleCategoryItemClick = useCallback(
        (item: Maybe<CategoryTree>, keepOpen = false) => {
            if (isEspaciosMegaMenu(item)) {
                setSubMenu({ back: '', content: null, seeAllURL: '' })
                // setDrillStack([])
                setEspaciosItem(item)
                setShowEspacios(true)
                return
            }

            // TODO: drill nuevo de categorías. Descomentar en lugar del
            // setSubMenu de abajo (solapa CMS de master, como PRODUCTOS).
            // if (item) {
            //     setSubMenu({ back: '', content: null, seeAllURL: '' })
            //     setDrillStack([{ title: item.name || '', item }])
            //     return
            // }

            setShowEspacios(false)
            setEspaciosItem(null)
            setSubMenu({
                back: item?.name,
                content: item?.cms_block?.content,
                seeAllURL: item?.external_url || `/categoria/${item?.url_path}`
            })
            setIsOpen(keepOpen)
        },
        [setIsOpen]
    )

    const categories = navData?.children
    if (!categories) return null

    const showRoot = !showEspacios && !subMenu.content
    const showHelpLinks = !showEspacios && !subMenu.content

    return (
        <div
            className={cn(s['megamenu-cms-mobile'], {
                [s.activeMobile]: isOpen,
                [s.inactiveMobile]: !isOpen
            })}
            inert={!isOpen}
        >
            <button
                type="button"
                className={s.backdrop}
                aria-label={t('Close panel')}
                tabIndex={isOpen ? 0 : -1}
                onClick={() => setIsOpen(false)}
            />
            <div className={s['megamenu-main-menu-wrap']}>
                <div className={cn(s['mobile-categories'])}>
                    <div className={s['menu-app-header']}>
                        <div className={s['menu-app-header-start']}>
                            <button
                                type="button"
                                className={cn(s['menu-app-burger'], {
                                    [s['menu-app-burger-open']]: isOpen
                                })}
                                aria-label={t('Close panel')}
                                onClick={() => setIsOpen(false)}
                            >
                                <svg
                                    width="50"
                                    height="42"
                                    viewBox="0 0 50 42"
                                    fill="none"
                                    aria-hidden
                                >
                                    <path
                                        className="burger-bars"
                                        d="M37.648 12.87C37.648 12.0802 37.0078 11.44 36.218 11.44H13.9244C13.1346 11.44 12.4944 12.0802 12.4944 12.87C12.4944 13.6598 13.1346 14.3 13.9244 14.3H36.218C37.0078 14.3 37.648 13.6598 37.648 12.87Z"
                                        fill="#534E46"
                                    />
                                    <path
                                        className="burger-bars"
                                        d="M37.6336 20.4967C37.6336 19.7069 36.9934 19.0667 36.2036 19.0667H13.91C13.1202 19.0667 12.48 19.7069 12.48 20.4967C12.48 21.2864 13.1202 21.9267 13.91 21.9267H36.2036C36.9934 21.9267 37.6336 21.2864 37.6336 20.4967Z"
                                        fill="#534E46"
                                    />
                                    <path
                                        className="burger-bars"
                                        d="M37.6336 28.1233C37.6336 27.3336 36.9934 26.6933 36.2036 26.6933H13.91C13.1202 26.6933 12.48 27.3336 12.48 28.1233C12.48 28.9131 13.1202 29.5533 13.91 29.5533H36.2036C36.9934 29.5533 37.6336 28.9131 37.6336 28.1233Z"
                                        fill="#534E46"
                                    />
                                </svg>
                            </button>
                            <Link
                                href="/"
                                className={s['menu-app-logo']}
                                aria-label={t('Logo Mobile')}
                            >
                                <PWALogo />
                            </Link>
                        </div>
                        <div className={s['menu-app-header-end']}>
                            <Searchbar id="menu-mobile-search" variant="header" />
                            <UserNav />
                        </div>
                    </div>
                    <div
                        className={s['menu-sheet-body']}
                        ref={categoriesContainer}
                    >
                    {showEspacios ? (
                        <EspaciosCasaMobilePanel
                            title={
                                espaciosItem?.name || 'Espacios de la casa'
                            }
                            navChildren={espaciosItem?.children}
                            onBack={() => {
                                setShowEspacios(false)
                                setEspaciosItem(null)
                            }}
                            onNavigate={() => setIsOpen(false)}
                        />
                    ) : null}
                    {/* TODO: descomentar el drill nuevo de categorías.
                    {drillStack.length > 0 && !showEspacios ? (
                        <CategoryMobileDrillPanel
                            level={drillStack[drillStack.length - 1]}
                            injectNovedades={drillStack.length === 1}
                            onBack={() =>
                                setDrillStack((stack) => stack.slice(0, -1))
                            }
                            onDrill={(item) =>
                                setDrillStack((stack) => [
                                    ...stack,
                                    { title: item.name || '', item }
                                ])
                            }
                            onNavigate={() => setIsOpen(false)}
                        />
                    ) : null}
                    */}
                    {showRoot
                        ? renderCategories(
                              categories,
                              handleCategoryItemClick
                          )
                        : null}
                    {subMenu.content && !showEspacios ? (
                        <div>
                            <div className={s['submenu-mobile-upper']}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={s['submenu-mobile-back']}
                                    onClick={() =>
                                        setSubMenu({
                                            back: '',
                                            content: null,
                                            seeAllURL: ''
                                        })
                                    }
                                    onKeyDown={() =>
                                        setSubMenu({
                                            back: '',
                                            content: null,
                                            seeAllURL: ''
                                        })
                                    }
                                >
                                    <p>{subMenu.back}</p>
                                </div>
                            </div>
                            <RichContent
                                html={subMenu.content}
                                classes={{ root: 'menuCmsContentMobile' }}
                                isLazyLoad={false}
                            />
                        </div>
                    ) : null}
                    </div>
                    {showHelpLinks ? (
                        <div className={s['megamenu-container-bottom']}>
                            <Link
                                href="https://ayuda.casaideas.cl/centro-ayuda/seguimiento-pedido/buscar"
                                rel="noreferrer"
                                target="_blank"
                            >
                                <MenuTrackOrder />
                                {t('footer|menu track order')}
                            </Link>
                            <Link
                                href={FOOTER_URLS.findStore}
                                rel="noreferrer"
                                target="_blank"
                            >
                                <MenuFindStore />
                                {t('footer|menu find store')}
                            </Link>
                            <Link
                                href={FOOTER_URLS.helpCenter}
                                rel="noreferrer"
                                target="_blank"
                            >
                                <MenuFaq />
                                {t('footer|menu faq')}
                            </Link>
                            <button type="button" onClick={openEvaChat}>
                                <MenuChat />
                                {t('footer|menu chat')}
                            </button>
                            <Link
                                href={FOOTER_URLS.helpCenter}
                                rel="noreferrer"
                                target="_blank"
                            >
                                <MenuHelpCenter />
                                {t('footer|menu help center')}
                            </Link>
                        </div>
                    ) : null}
                </div>

                <div className={cn(s['mobile-subcategories'])} />
            </div>
        </div>
    )
}

export default MobileMegaMenu
